import { Injectable } from '@angular/core';
import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  DocumentSnapshot,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { AuthenticationService } from 'ngx-core-services';
import { first } from 'rxjs/operators';
import { BehaviorSubject, Observable } from 'rxjs';
import { ChipData, FirmwareConfig } from 'ngx-modbus';

import { FirmwareTestStorageService, LoggerType } from '../firmware-test-storage/firmware-test-storage.service';


/**
 * Logger override data structure
 */
export interface LoggerOverrideData {
  ID?: string;
  DEVICE_MODEL_ID: LoggerType;
  LATEST_SOLARMAN_LOGGER_BY_WEB?: string;
  LATEST_SOLARMAN_LOGGER?: string;
  LINK_SOLARMAN_LOGGER?: string;
  MIN_SOLARMAN_LOGGER?: string;
  LAN_UPGRADE?: boolean;
}

/**
 * Safety override data structure
 */
export interface SafetyOverrideData {
  binaryLink?: string;        // Firebase URL for test binary
  customLinks: string[];      // Firebase URLs for custom .txt files
}

/**
 * Safety overrides by device pattern
 */
export type SafetyOverrides = {
  [devicePattern: string]: SafetyOverrideData;
};

// Re-export for backward compatibility
export { ChipData, FirmwareConfig };

/**
 * Type for firmware configuration overrides
 * Maps device pattern to module types to chip data arrays
 */
export type FirmwareConfigOverrides = {
  [devicePattern: string]: {
    [moduleType: string]: ChipData[] | { [batteryModule: string]: ChipData[] };
  };
};

/**
 * Validation result for overrides
 */
export interface ValidationResult {
  valid: boolean;
  invalidPaths: string[];
  cleanedOverrides: FirmwareConfigOverrides;
}

/**
 * Test configuration document structure
 */
interface TestConfigDocument {
  overrides: FirmwareConfigOverrides;
  safetyOverrides?: SafetyOverrides;  // NEW: Safety overrides
  lastModified: Timestamp;
  validatedAt?: Timestamp;
  active?: boolean;  // Test mode active/inactive state
}

@Injectable({ providedIn: 'root' })
export class TestConfigurationService {
  private firestore = getFirestore(getApp());
  private testModeEnabled$ = new BehaviorSubject<boolean>(false);
  private unsubscribeSnapshot?: () => void;

  constructor(
    private authService: AuthenticationService,
    private firmwareStorage: FirmwareTestStorageService
  ) {
    this.initTestModeListener();
  }

  /**
   * Initialize Firestore listener for test mode state
   * Fixed: Subscribe to user$ instead of using first() to handle late authentication
   */
  private initTestModeListener(): void {
    // Subscribe to user$ to handle authentication state changes
    this.authService.user$.subscribe((currentUser) => {
      // Clean up previous listener if exists
      if (this.unsubscribeSnapshot) {
        this.unsubscribeSnapshot();
        this.unsubscribeSnapshot = undefined;
      }

      if (!currentUser?.uid) {
        // User logged out, reset test mode to false
        this.testModeEnabled$.next(false);
        return;
      }

      try {
        const operatorId = currentUser.uid;
        const docRef = doc(this.firestore, 'test-configurations', operatorId);

        // Subscribe to real-time updates
        this.unsubscribeSnapshot = onSnapshot(docRef, (snapshot) => {
          const data = snapshot.data() as TestConfigDocument | undefined;
          const active = data?.active ?? false;
          this.testModeEnabled$.next(active);
        }, (error) => {
          console.error('[TestConfig] Snapshot listener error:', error);
        });
      } catch (error) {
        console.error('[TestConfig] Failed to initialize test mode listener:', error);
      }
    });
  }

  /**
   * Load overrides for current operator
   */
  async loadMyOverrides(): Promise<FirmwareConfigOverrides> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);
    const snapshot = await getDoc(docRef) as DocumentSnapshot<TestConfigDocument>;

    if (!snapshot.exists()) {
      return {};
    }

    const data = snapshot.data();
    return data?.overrides || {};
  }

  /**
   * Save override for a specific device pattern and module
   */
  async saveOverride(
    devicePattern: string,
    moduleType: string,
    chipData: ChipData
  ): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadMyOverrides();

    // Initialize structure if needed
    if (!existing[devicePattern]) {
      existing[devicePattern] = {};
    }

    // Handle battery sub-modules
    if (moduleType.startsWith('BATTERY.')) {
      const batteryModule = moduleType.replace('BATTERY.', '');

      if (!existing[devicePattern]['BATTERY']) {
        existing[devicePattern]['BATTERY'] = {};
      }

      const battery = existing[devicePattern]['BATTERY'] as { [key: string]: ChipData[] };

      if (!battery[batteryModule]) {
        battery[batteryModule] = [];
      }

      // Find and replace existing chip or add new
      const chipIndex = battery[batteryModule].findIndex(
        chip => chip.CHIP === chipData.CHIP
      );

      if (chipIndex >= 0) {
        battery[batteryModule][chipIndex] = chipData;
      } else {
        battery[batteryModule].push(chipData);
      }
    } else {
      // Regular module (ARM, DSPM, etc.)
      if (!existing[devicePattern][moduleType]) {
        existing[devicePattern][moduleType] = [];
      }

      const moduleChips = existing[devicePattern][moduleType] as ChipData[];

      // Find and replace existing chip or add new
      const chipIndex = moduleChips.findIndex(
        chip => chip.CHIP === chipData.CHIP
      );

      if (chipIndex >= 0) {
        moduleChips[chipIndex] = chipData;
      } else {
        moduleChips.push(chipData);
      }
    }

    // Save to Firestore
    await setDoc(docRef, {
      overrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Remove override for a specific chip
   */
  async removeOverride(
    devicePattern: string,
    moduleType: string,
    chipId: string
  ): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadMyOverrides();

    if (moduleType.startsWith('BATTERY.')) {
      // Handle battery sub-module
      const batteryModule = moduleType.replace('BATTERY.', '');
      const battery = existing[devicePattern]?.['BATTERY'] as { [key: string]: ChipData[] };

      if (battery?.[batteryModule]) {
        battery[batteryModule] = battery[batteryModule].filter(
          chip => chip.CHIP !== chipId
        );

        // Cleanup empty structures
        if (battery[batteryModule].length === 0) {
          delete battery[batteryModule];
        }
        if (Object.keys(battery).length === 0) {
          delete existing[devicePattern]['BATTERY'];
        }
      }
    } else {
      // Regular module
      const moduleChips = existing[devicePattern]?.[moduleType] as ChipData[];

      if (moduleChips) {
        existing[devicePattern][moduleType] = moduleChips.filter(
          chip => chip.CHIP !== chipId
        );

        // Cleanup empty structures
        if ((existing[devicePattern][moduleType] as ChipData[]).length === 0) {
          delete existing[devicePattern][moduleType];
        }
      }
    }

    // Cleanup empty pattern
    if (existing[devicePattern] && Object.keys(existing[devicePattern]).length === 0) {
      delete existing[devicePattern];
    }

    // Save to Firestore
    await setDoc(docRef, {
      overrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Remove all overrides for a specific device pattern
   */
  async removeOverridePattern(devicePattern: string): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadMyOverrides();

    // Remove entire pattern
    if (existing[devicePattern]) {
      delete existing[devicePattern];
    }

    // Save to Firestore
    await setDoc(docRef, {
      overrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Clear all overrides for current operator
   */
  async clearAllOverrides(): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    await setDoc(docRef, {
      overrides: {},
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Validate overrides: remove invalid Firebase paths
   * Uses parallel validation for better performance
   */
  async validateOverrides(): Promise<ValidationResult> {
    const overrides = await this.loadMyOverrides();
    const invalidPaths: string[] = [];
    const validatedOverrides: FirmwareConfigOverrides = JSON.parse(JSON.stringify(overrides));

    // Collect all paths to validate
    const pathsToValidate: Array<{ path: string; chip?: ChipData; location: any; isLogger?: boolean }> = [];
    const validLoggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];

    for (const [pattern, config] of Object.entries(overrides)) {
      const deviceModelId = (config as any).DEVICE_MODEL_ID;

      // Check if it's a LOGGER override
      if (validLoggerTypes.includes(deviceModelId)) {
        const loggerLink = (config as any).LINK_SOLARMAN_LOGGER;

        if (loggerLink && loggerLink.startsWith('firebase://')) {
          const path = loggerLink.replace('firebase://', '');
          pathsToValidate.push({
            path,
            location: { pattern },
            isLogger: true
          });
        }
        continue; // Skip module iteration for logger overrides
      }

      // Process firmware/inverter overrides
      for (const [moduleType, moduleData] of Object.entries(config)) {
        if (moduleType === 'BATTERY') {
          // Handle battery sub-modules
          const battery = moduleData as { [key: string]: ChipData[] };

          for (const [batteryModule, chips] of Object.entries(battery)) {
            for (const chip of chips) {
              if (chip.LINK && chip.LINK.startsWith('firebase://')) {
                const path = chip.LINK.replace('firebase://', '');
                pathsToValidate.push({
                  path,
                  chip,
                  location: { pattern, moduleType, batteryModule }
                });
              }
            }
          }
        } else {
          // Regular module
          const chips = moduleData as ChipData[];

          for (const chip of chips) {
            if (chip.LINK && chip.LINK.startsWith('firebase://')) {
              const path = chip.LINK.replace('firebase://', '');
              pathsToValidate.push({
                path,
                chip,
                location: { pattern, moduleType }
              });
            }
          }
        }
      }
    }

    // Validate all paths in parallel
    const validationResults = await Promise.all(
      pathsToValidate.map(async (item) => ({
        ...item,
        isValid: await this.firmwareStorage.validatePath(item.path)
      }))
    );

    // Process validation results
    const validPathsSet = new Set<string>();
    for (const result of validationResults) {
      if (result.isValid) {
        validPathsSet.add(result.path);
      } else {
        invalidPaths.push(result.path);
      }
    }

    // Rebuild validated config using validation results
    for (const [pattern, config] of Object.entries(overrides)) {
      const deviceModelId = (config as any).DEVICE_MODEL_ID;

      // Handle logger overrides
      if (validLoggerTypes.includes(deviceModelId)) {
        const loggerLink = (config as any).LINK_SOLARMAN_LOGGER;

        if (loggerLink && loggerLink.startsWith('firebase://')) {
          const path = loggerLink.replace('firebase://', '');
          if (!validPathsSet.has(path)) {
            // Invalid firebase path - remove the logger override
            delete validatedOverrides[pattern];
          }
          // else: keep it as is (valid firebase path)
        }
        // else: S3 production link or no link - keep it as is
        continue;
      }

      // Process firmware/inverter overrides
      for (const [moduleType, moduleData] of Object.entries(config)) {
        if (moduleType === 'BATTERY') {
          // Handle battery sub-modules
          const battery = moduleData as { [key: string]: ChipData[] };

          for (const [batteryModule, chips] of Object.entries(battery)) {
            const validChips: ChipData[] = [];

            for (const chip of chips) {
              if (chip.LINK && chip.LINK.startsWith('firebase://')) {
                const path = chip.LINK.replace('firebase://', '');
                if (validPathsSet.has(path)) {
                  validChips.push(chip);
                }
              } else if (chip.LINK) {
                // S3 production link - keep it
                validChips.push(chip);
              }
            }

            if (validChips.length > 0) {
              (validatedOverrides[pattern]['BATTERY'] as any)[batteryModule] = validChips;
            } else {
              delete (validatedOverrides[pattern]['BATTERY'] as any)[batteryModule];
            }
          }

          // Clean up empty BATTERY
          if (Object.keys(validatedOverrides[pattern]['BATTERY'] as any).length === 0) {
            delete validatedOverrides[pattern]['BATTERY'];
          }
        } else {
          // Regular module
          const chips = moduleData as ChipData[];
          const validChips: ChipData[] = [];

          for (const chip of chips) {
            if (chip.LINK && chip.LINK.startsWith('firebase://')) {
              const path = chip.LINK.replace('firebase://', '');
              if (validPathsSet.has(path)) {
                validChips.push(chip);
              }
            } else if (chip.LINK) {
              // S3 production link - keep it
              validChips.push(chip);
            }
          }

          if (validChips.length > 0) {
            validatedOverrides[pattern][moduleType] = validChips;
          } else {
            delete validatedOverrides[pattern][moduleType];
          }
        }
      }

      // Clean up empty pattern
      if (Object.keys(validatedOverrides[pattern] || {}).length === 0) {
        delete validatedOverrides[pattern];
      }
    }

    // Save validated overrides if there were changes
    if (invalidPaths.length > 0) {
      const currentUser = await this.authService.user$.pipe(first()).toPromise();
      if (currentUser?.uid) {
        const operatorId = currentUser.uid;
        const docRef = doc(this.firestore, 'test-configurations', operatorId);

        await setDoc(docRef, {
          overrides: validatedOverrides,
          validatedAt: serverTimestamp()
        } as Partial<TestConfigDocument>, { merge: true });
      }
    }

    return {
      valid: invalidPaths.length === 0,
      invalidPaths,
      cleanedOverrides: validatedOverrides
    };
  }

  // ========== LOGGER OVERRIDE METHODS ==========

  /**
   * Save logger override for a specific pattern
   */
  async saveLoggerOverride(
    devicePattern: string,
    overrideData: LoggerOverrideData
  ): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadMyOverrides();

    // Save logger override (flat structure, no nesting)
    // Use 'as any' to bypass type checking since FirmwareConfigOverrides is inverter-specific
    const loggerConfig: any = {
      DEVICE_MODEL_ID: overrideData.DEVICE_MODEL_ID
    };

    // Add optional fields only if provided
    if (overrideData.ID !== undefined) loggerConfig.ID = overrideData.ID;
    if (overrideData.LATEST_SOLARMAN_LOGGER_BY_WEB !== undefined) {
      loggerConfig.LATEST_SOLARMAN_LOGGER_BY_WEB = overrideData.LATEST_SOLARMAN_LOGGER_BY_WEB;
    }
    if (overrideData.LATEST_SOLARMAN_LOGGER !== undefined) {
      loggerConfig.LATEST_SOLARMAN_LOGGER = overrideData.LATEST_SOLARMAN_LOGGER;
    }
    if (overrideData.LINK_SOLARMAN_LOGGER !== undefined) {
      loggerConfig.LINK_SOLARMAN_LOGGER = overrideData.LINK_SOLARMAN_LOGGER;
    }
    if (overrideData.MIN_SOLARMAN_LOGGER !== undefined) {
      loggerConfig.MIN_SOLARMAN_LOGGER = overrideData.MIN_SOLARMAN_LOGGER;
    }
    if (overrideData.LAN_UPGRADE !== undefined) {
      loggerConfig.LAN_UPGRADE = overrideData.LAN_UPGRADE;
    }

    (existing as any)[devicePattern] = loggerConfig;

    await setDoc(docRef, {
      overrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Remove logger override for a specific pattern
   */
  async removeLoggerOverride(devicePattern: string): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadMyOverrides();
    delete existing[devicePattern];

    await setDoc(docRef, {
      overrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Get all logger overrides (filter by DEVICE_MODEL_ID)
   */
  async getLoggerOverrides(): Promise<Record<string, LoggerOverrideData>> {
    const allOverrides = await this.loadMyOverrides();
    const loggerOverrides: Record<string, LoggerOverrideData> = {};

    const validLoggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];

    for (const [pattern, override] of Object.entries(allOverrides)) {
      const deviceModelId = (override as any).DEVICE_MODEL_ID;

      // Check if it's a logger config (by DEVICE_MODEL_ID)
      if (validLoggerTypes.includes(deviceModelId)) {
        loggerOverrides[pattern] = override as unknown as LoggerOverrideData;
      }
    }

    return loggerOverrides;
  }

  /**
   * Merge logger override into production config (helper)
   */
  private mergeLoggerOverride(productionLogger: any, testOverride: LoggerOverrideData): void {
    // Override only if present in test config
    if (testOverride.ID !== undefined) {
      productionLogger.ID = testOverride.ID;
    }
    if (testOverride.LATEST_SOLARMAN_LOGGER_BY_WEB !== undefined) {
      productionLogger.LATEST_SOLARMAN_LOGGER_BY_WEB = testOverride.LATEST_SOLARMAN_LOGGER_BY_WEB;
    }
    if (testOverride.LATEST_SOLARMAN_LOGGER !== undefined) {
      productionLogger.LATEST_SOLARMAN_LOGGER = testOverride.LATEST_SOLARMAN_LOGGER;
    }
    if (testOverride.LINK_SOLARMAN_LOGGER !== undefined) {
      productionLogger.LINK_SOLARMAN_LOGGER = testOverride.LINK_SOLARMAN_LOGGER;
    }
    if (testOverride.MIN_SOLARMAN_LOGGER !== undefined) {
      productionLogger.MIN_SOLARMAN_LOGGER = testOverride.MIN_SOLARMAN_LOGGER;
    }
    if (testOverride.LAN_UPGRADE !== undefined) {
      productionLogger.LAN_UPGRADE = testOverride.LAN_UPGRADE;
    }
  }

  /**
   * Merge production config with overrides
   */
  mergeConfigWithOverrides(
    productionConfig: FirmwareConfig,
    overrides: FirmwareConfigOverrides
  ): FirmwareConfig {
    const merged: FirmwareConfig = JSON.parse(JSON.stringify(productionConfig));

    for (const [pattern, overrideData] of Object.entries(overrides)) {
      if (!merged[pattern]) {
        console.warn(`Pattern "${pattern}" not in production config, skipping`);
        continue;
      }

      const deviceModelId = (merged[pattern] as any).DEVICE_MODEL_ID;
      const validLoggerTypes: LoggerType[] = ['ZSM-WIFI-USB', 'ZSM-WIFI-EXT', 'ZSM-ETH-USB', 'ZSM-ETH-EXT'];

      // Check if it's a LOGGER override
      if (validLoggerTypes.includes(deviceModelId)) {
        this.mergeLoggerOverride(merged[pattern], overrideData as any);
        continue;
      }

      // Verify it's an inverter config
      if (deviceModelId !== 'INVERTER') {
        console.warn(`Pattern "${pattern}" is not an inverter or logger config, skipping`);
        continue;
      }

      // Deep merge for each module
      for (const [moduleType, moduleData] of Object.entries(overrideData)) {
        if (moduleType === 'BATTERY') {
          // Handle battery sub-modules
          const batteryOverrides = moduleData as { [key: string]: ChipData[] };

          if (!(merged[pattern] as any)['BATTERY']) {
            (merged[pattern] as any)['BATTERY'] = {};
          }

          for (const [batteryModule, chips] of Object.entries(batteryOverrides)) {
            if (!(merged[pattern] as any)['BATTERY'][batteryModule]) {
              (merged[pattern] as any)['BATTERY'][batteryModule] = [];
            }

            // Replace matching chips
            for (const overrideChip of chips) {
              const existingChips = (merged[pattern] as any)['BATTERY'][batteryModule] as ChipData[];
              const prodChipIndex = existingChips.findIndex(
                (chip: ChipData) => chip.CHIP === overrideChip.CHIP
              );

              if (prodChipIndex >= 0) {
                existingChips[prodChipIndex] = overrideChip;
              } else {
                existingChips.push(overrideChip);
              }
            }
          }
        } else {
          // Regular module
          const chips = moduleData as ChipData[];

          if (!(merged[pattern] as any)[moduleType]) {
            (merged[pattern] as any)[moduleType] = [];
          }

          // Replace matching chips
          for (const overrideChip of chips) {
            const existingChips = (merged[pattern] as any)[moduleType] as ChipData[];
            const prodChipIndex = existingChips.findIndex(
              (chip: ChipData) => chip.CHIP === overrideChip.CHIP
            );

            if (prodChipIndex >= 0) {
              existingChips[prodChipIndex] = overrideChip;
            } else {
              existingChips.push(overrideChip);
            }
          }
        }
      }
    }

    return merged;
  }

  // ========== TEST MODE STATE MANAGEMENT ==========

  /**
   * Get test mode state as Observable
   */
  observeTestMode(): Observable<boolean> {
    return this.testModeEnabled$.asObservable();
  }

  /**
   * Get current test mode state (async)
   */
  async getTestMode(): Promise<boolean> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      return false;
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);
    const snapshot = await getDoc(docRef) as DocumentSnapshot<TestConfigDocument>;

    if (!snapshot.exists()) {
      return false;
    }

    const data = snapshot.data();
    return data?.active ?? false;
  }

  /**
   * Set test mode state (saves to Firestore)
   */
  async setTestMode(enabled: boolean): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    await setDoc(docRef, {
      active: enabled,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  // ========== SAFETY OVERRIDE METHODS ==========

  /**
   * Load safety overrides for current operator
   */
  async loadSafetyOverrides(): Promise<SafetyOverrides> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);
    const snapshot = await getDoc(docRef) as DocumentSnapshot<TestConfigDocument>;

    if (!snapshot.exists()) {
      return {};
    }

    const data = snapshot.data();
    return data?.safetyOverrides || {};
  }

  /**
   * Save safety override for a specific device pattern
   */
  async saveSafetyOverride(
    devicePattern: string,
    override: SafetyOverrideData
  ): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadSafetyOverrides();
    existing[devicePattern] = override;

    await setDoc(docRef, {
      safetyOverrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Get safety overrides for all patterns
   */
  async getSafetyOverrides(): Promise<SafetyOverrides> {
    return this.loadSafetyOverrides();
  }

  /**
   * Remove safety override for a specific pattern
   */
  async removeSafetyOverride(devicePattern: string): Promise<void> {
    const currentUser = await this.authService.user$.pipe(first()).toPromise();
    if (!currentUser?.uid) {
      throw new Error('Utente non autenticato');
    }

    const operatorId = currentUser.uid;
    const docRef = doc(this.firestore, 'test-configurations', operatorId);

    const existing = await this.loadSafetyOverrides();
    delete existing[devicePattern];

    await setDoc(docRef, {
      safetyOverrides: existing,
      lastModified: serverTimestamp()
    } as Partial<TestConfigDocument>, { merge: true });
  }

  /**
   * Cleanup method (call on destroy if needed)
   */
  destroy(): void {
    if (this.unsubscribeSnapshot) {
      this.unsubscribeSnapshot();
    }
  }
}
